"use client";

import { Suspense, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import {
  ArrowRight,
  CreditCard,
  LoaderCircle,
  LogIn,
  UserPlus,
} from "lucide-react";
import EmailVerification from "supertokens-auth-react/recipe/emailverification";
import Session from "supertokens-auth-react/recipe/session";

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@workspace/ui/components/card";
import AccountPlanRepository from "@/core/account-plan/domain/account-plan.repository";
import AccountSubscriptionRepository from "@/core/account-plan/domain/account-subscription.repository";
import { AccountSubscriptionStatus } from "@/core/account-plan/domain/models";
import BillingRepository from "@/core/billing/domain/billing.repository";
import OnboardingRepository from "@/core/onboarding/repository";
import { request } from "@/lib/api/request-api.client";
import { buildVerifyEmailPath } from "@/lib/auth/redirect";
import {
  buildSubscribePath,
  getOnboardingPath,
  type PurchaseCycle,
  type PurchasePlanType,
} from "@/lib/purchase-flow";

function SubscribeContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const requestedPlan = searchParams.get("plan");
  const planType: PurchasePlanType =
    requestedPlan === "FREE" ||
    requestedPlan === "PERSONAL" ||
    requestedPlan === "ORGANIZATION"
      ? requestedPlan
      : "PERSONAL";
  const cycle: PurchaseCycle =
    searchParams.get("cycle") === "YEARLY" ? "YEARLY" : "MONTHLY";
  const intentPath = buildSubscribePath(planType, cycle);
  const authQuery = `redirectTo=${encodeURIComponent(intentPath)}`;

  const [isAnonymous, setIsAnonymous] = useState(false);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [activeAccountSlug, setActiveAccountSlug] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const resolveDestination = async () => {
      if (!(await Session.doesSessionExist())) {
        if (active) setIsAnonymous(true);
        return;
      }

      const verification = await EmailVerification.isEmailVerified();
      if (!verification.isVerified) {
        router.replace(buildVerifyEmailPath(intentPath));
        return;
      }

      const statusResult = await new OnboardingRepository(request).getStatus();
      if (statusResult.isErr()) {
        if (active)
          setError("Não foi possível identificar o progresso da sua conta.");
        return;
      }
      const status = statusResult.value;
      if (!status.essentialCompleted || !status.accountSlug) {
        router.replace(getOnboardingPath(status.nextStep, intentPath));
        return;
      }

      const dashboardPath = `/account/${status.accountSlug}/platform/dashboard`;
      if (planType === "FREE") {
        router.replace(
          status.profileCompleted
            ? dashboardPath
            : getOnboardingPath(status.nextStep, dashboardPath),
        );
        return;
      }

      const plansResult = await new AccountPlanRepository(
        request,
      ).getAllActive();
      if (plansResult.isErr()) {
        if (active) setError("Não foi possível carregar o plano escolhido.");
        return;
      }
      const selectedPlan = plansResult.value.find(
        (item) => item.planType === planType,
      );
      if (!selectedPlan?.id) {
        if (active)
          setError(
            `O plano ${planType === "ORGANIZATION" ? "Conta Organização" : "Pessoal"} não está disponível neste momento.`,
          );
        return;
      }

      const subscriptionsResult = await new AccountSubscriptionRepository(
        request,
      ).getAll(status.accountSlug);
      if (subscriptionsResult.isOk()) {
        const hasActiveSubscription = subscriptionsResult.value.some(
          (subscription) =>
            subscription.status === AccountSubscriptionStatus.ACTIVE ||
            subscription.status ===
              AccountSubscriptionStatus.CANCEL_AT_PERIOD_END,
        );
        if (hasActiveSubscription) {
          if (active) setActiveAccountSlug(status.accountSlug);
          return;
        }
      }

      const purchaseResult = await new BillingRepository(
        request,
      ).getPurchaseState(status.accountSlug, selectedPlan.id, cycle);
      if (purchaseResult.isOk() && purchaseResult.value.status === "ACTIVE") {
        if (active) setActiveAccountSlug(status.accountSlug);
        return;
      }
      if (purchaseResult.isOk() && purchaseResult.value.checkoutUrl) {
        if (active) setResumeUrl(purchaseResult.value.checkoutUrl);
        return;
      }

      router.replace(
        `/account/${status.accountSlug}/plans/checkout?planId=${encodeURIComponent(
          selectedPlan.id,
        )}&cycle=${cycle}&from=subscribe`,
      );
    };

    void resolveDestination().catch(() => {
      if (active) setError("Não foi possível continuar a contratação.");
    });
    return () => {
      active = false;
    };
  }, [cycle, intentPath, planType, router]);

  if (isAnonymous) {
    return (
      <main className="flex min-h-screen items-center bg-muted/30 px-4 py-10 text-foreground">
        <Card className="mx-auto w-full max-w-lg">
          <CardHeader>
            <CardTitle>Continue com sua conta GeoOffice</CardTitle>
            <CardDescription>
              Sua escolha de plano e periodicidade ficará preservada.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Button size="lg" asChild>
              <Link href={`/auth/signup?${authQuery}`}>
                <UserPlus /> Criar conta <ArrowRight />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link href={`/auth/login?${authQuery}`}>
                <LogIn /> Já tenho conta
              </Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center bg-muted/30 px-4 py-10 text-foreground">
      <Card className="mx-auto w-full max-w-lg">
        <CardHeader>
          <CardTitle>
            {activeAccountSlug
              ? "Plano já contratado"
              : "Preparando sua contratação"}
          </CardTitle>
          <CardDescription>
            {resumeUrl
              ? "Encontramos um checkout em andamento."
              : "Estamos validando sua conta e o plano selecionado."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Não foi possível continuar</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          {resumeUrl ? (
            <Button size="lg" className="w-full" asChild>
              <a href={resumeUrl}>
                <CreditCard /> Retomar pagamento <ArrowRight />
              </a>
            </Button>
          ) : activeAccountSlug ? (
            <Button size="lg" className="w-full" asChild>
              <Link
                href={`/account/${activeAccountSlug}/platform/settings/billing`}
              >
                Gerenciar assinatura <ArrowRight />
              </Link>
            </Button>
          ) : !error ? (
            <p
              className="flex items-center gap-2 text-sm text-muted-foreground"
              role="status"
            >
              <LoaderCircle className="size-4 animate-spin" /> Carregando...
            </p>
          ) : null}
        </CardContent>
      </Card>
    </main>
  );
}

export default function SubscribePage() {
  return (
    <Suspense fallback={null}>
      <SubscribeContent />
    </Suspense>
  );
}
